/**
 * Z-index layers used throughout the app:
 *
 *   0–10    Base content
 *   10–30   Local layering, sticky headers
 *   30–50   Layout structure (sidebar, nav, content areas)
 *   50–80   Schedule/roster sticky headers, sidebar panels
 *   80000   Overlays (modals, dropdowns, dialogs, sheets, notifications, popovers)
 *   90000   Fullscreen editors (email, signature)
 *   100000+ Critical system overlays (integration alerts, annotations)
 *
 * Nuxt UI portaled components (dropdown menus, modals, popovers, tooltips, etc.)
 * must use z-80000 to sit alongside existing overlay components.
 */

const overlayZ = 'z-80000'

export default defineAppConfig({
  ui: {
    colors: {
      primary: 'primary',
      neutral: 'gray',
    },
    button: {
      defaultVariants: {
        size: 'md',
        variant: 'subtle',
        color: 'neutral',
      },
      variants: {
        size: {
          sm: {
            leadingIcon: 'size-3.5',
            trailingIcon: 'size-3.5',
          },
          md: {
            leadingIcon: 'size-3.5',
            trailingIcon: 'size-3.5',
          }
        }
      }
    },
    dropdownMenu: {
      slots: {
        content: overlayZ,
      },
    },
    contextMenu: {
      slots: {
        content: overlayZ,
      },
    },
    popover: {
      slots: {
        content: overlayZ,
      },
    },
    tooltip: {
      slots: {
        content: overlayZ,
      },
    },
    modal: {
      slots: {
        overlay: overlayZ,
        content: overlayZ,
      },
    },
    slideover: {
      slots: {
        overlay: overlayZ,
        content: overlayZ,
      },
    },
    drawer: {
      slots: {
        overlay: overlayZ,
        content: overlayZ,
      },
    },
    input: {
      slots: {
        root: 'w-full',
      },
      variants: {
        size: {
          md: {
            leadingIcon: 'size-4',
            base: 'py-2 text-sm/3'
          }
        }
      }
    },
    textarea: {
      slots: {
        root: 'w-full',
      },
    },
    inputNumber: {
      slots: {
        root: 'w-full',
      },
      variants: {
        size: {
          md: {
            leadingIcon: 'size-4',
            base: 'py-2 text-sm/3'
          }
        }
      }
    },
    inputDate: {
      variants: {
        size: {
          md: {
            leadingIcon: 'size-4',
            base: 'py-2 text-sm/3'
          }
        }
      }
    },
    inputTime: {
      variants: {
        size: {
          md: {
            leadingIcon: 'size-4',
            base: 'py-2 text-sm/3'
          }
        }
      }
    },
    select: {
      slots: {
        base: 'w-full',
        content: overlayZ,
      },
      variants: {
        size: {
          md: {
            leadingIcon: 'size-4',
            base: 'py-2 text-sm/3'
          }
        }
      }
    },
    selectMenu: {
      slots: {
        root: 'w-full',
        content: overlayZ,
        base: 'w-full',
      },
    },
    inputMenu: {
      slots: {
        root: 'w-full',
        content: overlayZ,
      },
      variants: {
        size: {
          md: {
            leadingIcon: 'size-4',
            base: 'py-2 text-sm/3'
          }
        }
      }
    },
    navigationMenu: {
      slots: {
        label: 'text-white dark:text-white',
        linkLeadingIcon: 'text-white dark:text-white',
        linkLabelExternalIcon: 'text-white dark:text-white',
        childLinkLabelExternalIcon: 'text-white dark:text-white',
      },
      compoundVariants: [{
        variant: 'pill',
        active: true,
        highlight: false,
        class: {
          link: 'before:bg-white/10 dark:before:bg-neutral-800/70'
        }
      }, {
        variant: 'pill',
        active: false,
        highlight: false,
        class: {
          link: 'hover:before:bg-white/10 hover:dark:before:bg-neutral-800/70',
          linkLeadingIcon: 'text-neutral-700 dark:text-neutral-500',
          linkLabel: 'text-neutral-600 dark:text-neutral-300 text-[0.8rem]'
        }
      },{
        orientation: 'vertical',
        collapsed: false,
        class: {
          childList: 'ms-5 border-s border-white/10',
        }
      }]
    },
    table: {

    },
    icons: {
      arrowDown: 'i-lucide-arrow-down',
      arrowLeft: 'i-lucide-arrow-left',
      arrowRight: 'i-lucide-arrow-right',
      arrowUp: 'i-lucide-arrow-up',
      caution: 'i-lucide-circle-alert',
      check: 'i-lucide-check',
      chevronDoubleLeft: 'i-lucide-chevrons-left',
      chevronDoubleRight: 'i-lucide-chevrons-right',
      chevronDown: 'i-lucide-chevron-down',
      chevronLeft: 'i-lucide-chevron-left',
      chevronRight: 'i-lucide-chevron-right',
      chevronUp: 'i-lucide-chevron-up',
      close: 'i-lucide-x',
      copy: 'i-lucide-copy',
      copyCheck: 'i-lucide-copy-check',
      dark: 'i-lucide-moon',
      drag: 'i-lucide-grip-vertical',
      ellipsis: 'i-lucide-ellipsis',
      error: 'i-lucide-circle-x',
      external: 'i-lucide-arrow-up-right',
      eye: 'i-lucide-eye',
      eyeOff: 'i-lucide-eye-off',
      file: 'i-lucide-file',
      folder: 'i-lucide-folder',
      folderOpen: 'i-lucide-folder-open',
      hash: 'i-lucide-hash',
      info: 'i-lucide-info',
      light: 'i-lucide-sun',
      loading: 'i-lucide-loader-circle',
      menu: 'i-lucide-menu',
      minus: 'i-lucide-minus',
      panelClose: 'i-lucide-panel-left-close',
      panelOpen: 'i-lucide-panel-left-open',
      plus: 'i-lucide-plus',
      reload: 'i-lucide-rotate-ccw',
      search: 'i-lucide-search',
      stop: 'i-lucide-square',
      success: 'i-lucide-circle-check',
      system: 'i-lucide-monitor',
      tip: 'i-lucide-lightbulb',
      upload: 'i-lucide-upload',
      warning: 'i-lucide-triangle-alert'
    }
  },
})
